import { roundTo2Decimals } from '@principle-theorem/accounting';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  IMigratedDataSummary,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  MigratedWithErrorsDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  AccountCredit,
  Brand,
  BuildAccountAggregates,
  Invoice,
  Patient,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  AccountCreditType,
  IManualTransactionType,
  IPractice,
  ITreatmentConfiguration,
  type IAccountCredit,
  type IBrand,
  type IInvoice,
  type IPatient,
  type IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  doc,
  getError,
  omitByKeys,
  resolveSequentially,
  safeCombineLatest,
  snapshotCombineLatest,
  toTimestamp,
  type DocumentReference,
  type IIdentifiable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, partition, sumBy } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { PATIENT_TRANSACTION_RESOURCE_TYPE } from '../../../destination/entities/patient-invoices';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { ItemCodeResourceMapType } from '../../../mappings/item-codes-to-xlsx';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { AgreementHoldersV2SourceEntity } from '../../source/entities/agreement-holders-v2';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import { PatientBalancesV2SourceEntity } from '../../source/entities/patient-balances-v2';
import { PatientInvoiceLinesV2SourceEntity } from '../../source/entities/patient-invoice-lines-v2';
import {
  type IExactV2Transaction,
  PatientTransactionsV2SourceEntity,
} from '../../source/entities/patient-transactions-v2';
import { TransactionAllocationsV2SourceEntity } from '../../source/entities/transaction-allocations-v2';
import { getBasicIdFromExactId } from '../../util/helpers';
import { ExactItemCodeToTreatmentMappingHandler } from '../mappings/item-code-to-treatment';
import { ExactItemCodeMappingHandler } from '../mappings/item-codes';
import { ExactPaymentMethodMappingHandler } from '../mappings/payment-method';
import { ExactPayorLabelMappingHandler } from '../mappings/payor-label';
import { StaffToPracticeMapping } from '../mappings/practitioner-to-practice-mapping';
import { ExactStafferMappingHandler } from '../mappings/staff';

import {
  ExactInvoiceBuilderV2,
  IInvoiceBuildData,
  getExactV2PendingClaimTotal,
} from './lib/exact-invoice-builder-v2';
import { ExactOpenCredit } from './lib/exact-open-credit';
import { EXACT_MINIMUM_OWING } from './lib/exact-transaction-ledger';
import { PatientDestinationEntity } from './patient';

interface IInvoiceUpdateData {
  invoiceRef: DocumentReference<IInvoice>;
  transactionRefs: DocumentReference<ITransaction>[];
  accountCreditRefs: DocumentReference<IAccountCredit>[];
}

/**
 * Flags a patient whose built invoices didn't reconcile to the Exact-reported
 * balance. Neither direction is closed automatically: fabricating an invoice
 * or a credit would hand the practice invented debt or the patient spendable
 * money, and hide the modelling defect responsible. Surfaced as
 * MigratedWithErrors.
 */
interface IBalanceDiscrepancyFailData {
  patientId: string;
  missingAmount: number;
  expectedBalance: number;
}

const OVERPAYMENT_ACCOUNT_CREDIT_PREFIX = 'overpayment-';

const CREDIT_NOTE_ACCOUNT_CREDIT_PREFIX = `${OVERPAYMENT_ACCOUNT_CREDIT_PREFIX}credit-note-`;

export interface IExactV2AccountCreditInput {
  uid: string;
  description: string;
  amount: number;
}

export interface IExactV2CreditNoteSplit {
  invoices: IInvoiceBuildData[];
  accountCredits: IExactV2AccountCreditInput[];
}

/**
 * Exact normally credits an account with a payment-side type. A negative
 * Charge or unallocated invoice-side adjustment lands in the charges bucket
 * instead and becomes an invoice that still holds credit.
 */
function isExactV2CreditNote({
  invoice,
  transactions,
}: IInvoiceBuildData): boolean {
  return (
    roundTo2Decimals(Invoice.total(invoice)) < 0 &&
    roundTo2Decimals(Invoice.balance(invoice, transactions)) < 0
  );
}

function toExactV2CreditNote({
  invoice,
  transactions,
}: IInvoiceBuildData): IExactV2AccountCreditInput {
  return {
    uid: `${CREDIT_NOTE_ACCOUNT_CREDIT_PREFIX}${invoice.uid}`,
    description: `Credit note from Exact (${invoice.uid})`,
    amount: roundTo2Decimals(-Invoice.balance(invoice, transactions)),
  };
}

/**
 * Splits out the invoices Exact left holding credit. Uses balance not total,
 * because Exact may have allocated part of the negative charge elsewhere —
 * only the unallocated remainder is still owed back.
 */
export function extractExactV2CreditNotes(
  invoiceData: IInvoiceBuildData[]
): IExactV2CreditNoteSplit {
  const [creditNotes, invoices] = partition(invoiceData, isExactV2CreditNote);
  return { invoices, accountCredits: creditNotes.map(toExactV2CreditNote) };
}

/**
 * Resolve the AccountCredits needed to reach the account's negative debtacc3
 * balance. `creditNotes` already count towards that target, so they're
 * subtracted from it — double-counting makes reconciliation charge the patient.
 */
export function buildExactV2AccountCreditInputs(
  expectedBalance: number,
  patientId: string,
  creditCandidates: IExactV2Transaction[],
  creditNotes: IExactV2AccountCreditInput[] = []
): IExactV2AccountCreditInput[] {
  const creditNoteTotal = roundTo2Decimals(
    sumBy(creditNotes, (creditNote) => creditNote.amount)
  );
  const targetCreditTotal = roundTo2Decimals(
    Math.max(0, -expectedBalance - creditNoteTotal)
  );
  if (targetCreditTotal === 0) {
    return creditNotes;
  }

  const sortedCreditCandidates = creditCandidates
    .filter((transaction) => ExactOpenCredit.isRefundableCredit(transaction))
    .sort((aTransaction, bTransaction) => {
      const dateComparison = aTransaction.date.localeCompare(bTransaction.date);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      return aTransaction.id.localeCompare(bTransaction.id);
    });

  const result = sortedCreditCandidates.reduce<{
    remaining: number;
    credits: IExactV2AccountCreditInput[];
  }>(
    (state, transaction) => {
      if (state.remaining <= 0) {
        return state;
      }

      const availableAmount = roundTo2Decimals(
        Math.abs(transaction.amountOpen)
      );
      const amount = roundTo2Decimals(
        Math.min(availableAmount, state.remaining)
      );
      if (amount <= 0) {
        return state;
      }

      return {
        remaining: roundTo2Decimals(state.remaining - amount),
        credits: [
          ...state.credits,
          {
            uid: `${OVERPAYMENT_ACCOUNT_CREDIT_PREFIX}${transaction.id}`,
            description: `Overpayment transaction ${transaction.id}`,
            amount,
          },
        ],
      };
    },
    { remaining: targetCreditTotal, credits: [] }
  );

  if (result.remaining <= 0) {
    return [...creditNotes, ...result.credits];
  }

  return [
    ...creditNotes,
    ...result.credits,
    {
      uid: `${OVERPAYMENT_ACCOUNT_CREDIT_PREFIX}balance-${patientId}`,
      description: `Opening credit balance from debtacc3 (${patientId})`,
      amount: result.remaining,
    },
  ];
}

export const PATIENT_INVOICE_RESOURCE_TYPE_V2 = 'patientInvoicesV2';

export const PATIENT_INVOICE_V2_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_INVOICE_RESOURCE_TYPE_V2,
    label: 'Patient Invoices V2',
    description: `
      V2 invoice migration using raw Exact DB dump tables (the flat ledger,
      the line-item table, and the double-entry allocation table:
      TRANS4, INVLDNT5, TRNSALLO).

      Improvements over V1:
      - Deterministic receipt→invoice allocation from the source allocation
        table (replaces greedy oldest-first).
      - No description string parsing — all data is typed columns.
      - Agreement-funded transactions (ACC/MOH/GDB/SDB) are attributed to the
        real patient and labelled with the payor via the ExactPayorLabel mapping.
      - Adjustments are routed by typed enum (invoice-side vs receipt-side)
        instead of magic strings.
      - Invoice line items come from the per-tooth, per-service line-item
        table rather than parsed descriptions.
    `,
  },
});

interface IPatientInvoiceV2MigrationData {
  patientRef: DocumentReference<IPatient>;
  data: IInvoiceBuildData[];
  createdAt: Timestamp;
  /** debtacc3 source-of-truth balance for post-write reconciliation. */
  expectedBalance: number;
  /** Plain patient id (stripped form) for failData reporting. */
  patientId: string;
  /**
   * Raw credit-capable transactions for this patient with amountOpen < 0.
   * Used in runJob to write Overpayment AccountCredit records so the patient's
   * credit balance matches debtacc3.
   */
  sourceCreditTransactions: IExactV2Transaction[];
  /**
   * Credits replacing negative-total invoices. Carried across because the
   * invoices they came from are gone by the time runJob rebuilds the set.
   */
  creditNotes?: IExactV2AccountCreditInput[];
  balanceDiscrepancy?: {
    missingAmount: number;
    expectedBalance: number;
  };
  /** Legacy persisted migrationData field from older runs. */
  sourceReceipts?: IExactV2Transaction[];
}

export interface IPatientInvoiceV2DestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord<ExactPatient>>;
  invoiceData: {
    invoiceRef: DocumentReference<IInvoice>;
    transactionRefs: DocumentReference<ITransaction>[];
  }[];
}

export interface IPatientInvoiceV2JobData {
  sourcePatient: IGetRecordResponse<ExactPatient>;
  brand: WithRef<IBrand>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: WithRef<IStaffer>[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
  sourceItemCodesToTreatments: WithPrimaryKey<
    ITranslationMap<ITreatmentConfiguration>
  >[];
  staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[];
  payorLabels: WithPrimaryKey<
    ITranslationMap<IManualTransactionType, string>
  >[];
  paymentMethods: WithPrimaryKey<
    ITranslationMap<IManualTransactionType, string>
  >[];
}

export class PatientInvoicesV2DestinationEntity extends BaseDestinationEntity<
  IPatientInvoiceV2DestinationRecord,
  IPatientInvoiceV2JobData,
  IPatientInvoiceV2MigrationData
> {
  destinationEntity = PATIENT_INVOICE_V2_DESTINATION_ENTITY;

  sourceCountComparison = new PatientSourceEntity();

  override canMigrateByIdRange = true;

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    transactions: new PatientTransactionsV2SourceEntity(),
    invoiceLines: new PatientInvoiceLinesV2SourceEntity(),
    allocations: new TransactionAllocationsV2SourceEntity(),
    balances: new PatientBalancesV2SourceEntity(),
    agreementHolders: new AgreementHoldersV2SourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  override filters = [
    new PatientIdFilter<IPatientInvoiceV2JobData>(
      (data) => data.sourcePatient.data.patient_id
    ),
  ];

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    itemCodes: new ExactItemCodeMappingHandler(),
    itemCodeToTreatments: new ExactItemCodeToTreatmentMappingHandler(),
    staffToPractice: new StaffToPracticeMapping(),
    payorLabels: new ExactPayorLabelMappingHandler(),
    paymentMethods: new ExactPaymentMethodMappingHandler(),
  };

  sourceCountDataAccessor(
    data: IPatientInvoiceV2JobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IPatientInvoiceV2DestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return combineLatest(
      record.data.invoiceData.map((data) =>
        combineLatest([
          Firestore.getDoc(data.invoiceRef),
          safeCombineLatest(
            data.transactionRefs.map((transactionRef) =>
              Firestore.getDoc(transactionRef)
            )
          ),
        ])
      )
    ).pipe(
      map(([invoiceRef, transactions]) => [
        {
          label: 'Invoice',
          data: invoiceRef,
        },
        ...transactions.map((transaction) => ({
          label: 'Transaction',
          data: transaction,
        })),
      ])
    );
  }

  getDestinationEntityRecordUid(data: IPatientInvoiceV2JobData): string {
    return data.sourcePatient.record.uid;
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInvoiceV2JobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration);
    const practitioners$ = brand$.pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand)))
    );

    return snapshotCombineLatest([
      staff$,
      brand$,
      practitioners$,
      this.customMappings.itemCodes.getRecords(translationMap),
      this.customMappings.itemCodeToTreatments.getRecords(translationMap),
      this.customMappings.staffToPractice.getRecords(translationMap),
      this.customMappings.payorLabels.getRecords(translationMap),
      this.customMappings.paymentMethods.getRecords(translationMap),
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          patients,
          [
            staff,
            brand,
            practitioners,
            sourceItemCodes,
            sourceItemCodesToTreatments,
            staffToPractice,
            payorLabels,
            paymentMethods,
          ],
        ]) =>
          patients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            brand,
            practitioners,
            sourceItemCodes,
            sourceItemCodesToTreatments,
            staffToPractice,
            payorLabels,
            paymentMethods,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInvoiceV2JobData
  ): Promise<
    | IPatientInvoiceV2MigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    const patientId = data.sourcePatient.data.patient_id;
    /**
     * V2 source entities key on the plain patient id (e.g. '1234'), while
     * the V1 patient entity uses a suffixed form (e.g. '1234-19-0').
     * `getBasicIdFromExactId` strips the suffix back to the numeric portion.
     */
    const basicPatientId = String(getBasicIdFromExactId(patientId));

    /**
     * Agreement-holder accounts share Exact's numeric id-space with patient
     * debtor ids, so a "patient" row can actually be a holder ledger. Probe
     * the holder set with this patient's basic id; if it matches, skip.
     * Doing this here rather than in buildJobData$ keeps buildJobData$ free
     * of batched Postgres sources (which would stall snapshotCombineLatest).
     */
    const holderMatches = await this.sourceEntities.agreementHolders
      .filterRecords(migration, 'agreementHolderId', basicPatientId)
      .catch(() => []);
    if (holderMatches.length) {
      return this.buildSkippedResponse(data);
    }

    const patientRef = await translationMap.getDestination<IPatient>(
      patientId,
      PATIENT_RESOURCE_TYPE
    );
    if (!patientRef) {
      return this.buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    const patient = await Firestore.getDoc(patientRef);

    const patientTransactions =
      await this.sourceEntities.transactions.filterRecords(
        migration,
        'patientId',
        basicPatientId
      );

    if (!patientTransactions.length) {
      return {
        patientRef: patient.ref,
        data: [],
        createdAt: Timestamp.now(),
        expectedBalance: 0,
        patientId: basicPatientId,
        sourceCreditTransactions: [],
      };
    }

    const patientBalances = await this.sourceEntities.balances.filterRecords(
      migration,
      'patientId',
      basicPatientId
    );

    const balance = patientBalances.length
      ? sumBy(patientBalances, (patientBalance) => patientBalance.data.balance)
      : 0;

    const patientInvoiceLines =
      await this.sourceEntities.invoiceLines.filterRecords(
        migration,
        'patientId',
        basicPatientId
      );

    /**
     * Fetch only this patient's allocations by patientId (keyed to the
     * charge/CR side). Previously this was passed as a global preload in
     * jobData, which stalled the job runner and truncated the allocation set.
     */
    const patientAllocations =
      await this.sourceEntities.allocations.filterRecords(
        migration,
        'patientId',
        basicPatientId
      );

    const invoiceBuilder = new ExactInvoiceBuilderV2(
      patientTransactions,
      patientInvoiceLines.map((row) => row.data),
      patientAllocations.map((row) => row.data),
      balance,
      data.staff,
      data.sourceItemCodes,
      data.sourceItemCodesToTreatments,
      data.staffToPractice,
      data.payorLabels,
      data.paymentMethods
    );

    try {
      const invoiceData = await invoiceBuilder.buildInvoices(
        patient,
        translationMap,
        migration.configuration.practices
      );

      const { invoices, accountCredits: creditNotes } =
        extractExactV2CreditNotes(invoiceData);

      const sourceCreditTransactions = patientTransactions
        .map((transaction) => transaction.data)
        .filter((transaction) =>
          ExactOpenCredit.isRefundableCredit(transaction)
        );

      /**
       * When the built invoices plus account credits don't match the
       * Exact-reported balance, the difference isn't represented by any raw
       * ledger row we can map, so the patient is flagged for review in either
       * direction rather than corrected with fabricated data.
       *
       * Unpaid payor claims are excluded: Exact parks those on the payor's own
       * debtor account, so its balance legitimately omits what the invoices
       * carry.
       */
      const pendingClaimTotal = getExactV2PendingClaimTotal(invoices);
      const invoiceBalanceSum = roundTo2Decimals(
        sumBy(invoices, ({ invoice, transactions }) =>
          Invoice.balance(invoice, transactions)
        ) - pendingClaimTotal
      );
      const accountCreditTotal = roundTo2Decimals(
        sumBy(
          buildExactV2AccountCreditInputs(
            balance,
            basicPatientId,
            sourceCreditTransactions,
            creditNotes
          ),
          (credit) => credit.amount
        )
      );
      const computedBalance = roundTo2Decimals(
        invoiceBalanceSum - accountCreditTotal
      );
      const missingAmount = roundTo2Decimals(balance - computedBalance);
      const hasBalanceDiscrepancy =
        Math.abs(missingAmount) > EXACT_MINIMUM_OWING;

      return {
        patientRef: patient.ref,
        data: invoices,
        createdAt: Timestamp.now(),
        expectedBalance: balance,
        patientId: basicPatientId,
        sourceCreditTransactions,
        creditNotes,
        ...(hasBalanceDiscrepancy
          ? {
              balanceDiscrepancy: {
                missingAmount,
                expectedBalance: balance,
              },
            }
          : {}),
      };
    } catch (error) {
      return this.buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientInvoiceV2JobData,
    _migrationData: IPatientInvoiceV2MigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<ExactPatient>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage: errorMessage ?? 'Missing required properties for patient',
      failData: {
        patientRef: patient.id,
      },
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IPatientInvoiceV2JobData,
    migrationData: IPatientInvoiceV2MigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const invoiceRefs = await this._upsertInvoices(
        migrationData,
        translationMap,
        migrationData.patientRef
      );

      /**
       * Write Overpayment AccountCredits to exactly match any negative
       * debtacc3 balance. Individual amountOpen rows are useful audit
       * candidates, but debtacc3 is the account-level source of truth.
       */
      const creditCandidates =
        migrationData.sourceCreditTransactions ??
        (migrationData.sourceReceipts ?? []).filter((transaction) =>
          ExactOpenCredit.isRefundableCredit(transaction)
        );
      const accountCreditInputs = buildExactV2AccountCreditInputs(
        migrationData.expectedBalance,
        migrationData.patientId,
        creditCandidates,
        migrationData.creditNotes ?? []
      );
      await this._upsertOverpaymentAccountCredits(
        migrationData.patientRef,
        accountCreditInputs
      );

      const accountAggregateBuilder = new BuildAccountAggregates(console);
      await accountAggregateBuilder.updatePatient(
        migrationData.patientRef,
        false
      );

      if (migrationData.balanceDiscrepancy) {
        return this._buildBalanceDiscrepancyResponse(
          jobData.sourcePatient,
          invoiceRefs,
          {
            patientId: migrationData.patientId,
            missingAmount: migrationData.balanceDiscrepancy.missingAmount,
            expectedBalance: migrationData.balanceDiscrepancy.expectedBalance,
          }
        );
      }

      return this._buildSuccessResponse(jobData.sourcePatient, invoiceRefs);
    } catch (error) {
      return this.buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    migrationData: IPatientInvoiceV2MigrationData
  ): Promise<IPatientInvoiceV2MigrationData | undefined> {
    const invoiceMergeConflicts: (IInvoiceBuildData | undefined)[] =
      await resolveSequentially(
        migrationData.data,
        async (invoiceBuildData) => {
          try {
            const invoiceRef = await translationMap.getDestination<IInvoice>(
              invoiceBuildData.invoice.uid,
              PATIENT_TRANSACTION_RESOURCE_TYPE
            );

            if (!invoiceRef) {
              return;
            }

            const existingInvoice = await Firestore.getDoc(invoiceRef);
            const invoiceMergeConflict = hasMergeConflicts(
              omitByKeys(invoiceBuildData.invoice, ['from', 'to']),
              omitByKeys(existingInvoice, ['from', 'to']),
              [],
              [
                {
                  key: 'transactions',
                  sortByPath: 'reference',
                },
              ]
            );

            const existingTransactions: (IIdentifiable & ITransaction)[] = [];

            const transactionMergeConflicts = await resolveSequentially(
              invoiceBuildData.transactions,
              async (transaction) => {
                try {
                  const existingTransaction = await Firestore.getDoc(
                    doc(
                      Invoice.transactionCol(existingInvoice),
                      transaction.uid
                    )
                  );

                  existingTransactions.push({
                    ...existingTransaction,
                  });

                  return hasMergeConflicts(transaction, existingTransaction);
                } catch {
                  return false;
                }
              }
            );

            const hasMergeConflict = [
              invoiceMergeConflict,
              ...transactionMergeConflicts,
            ].some((mergeConflict) => mergeConflict);

            if (hasMergeConflict) {
              return {
                ...invoiceBuildData,
                invoice: {
                  ...existingInvoice,
                  uid: invoiceBuildData.invoice.uid,
                },
                transactions: existingTransactions,
              };
            }
          } catch {
            return;
          }
        }
      );

    if (invoiceMergeConflicts.some((conflict) => conflict)) {
      return {
        ...migrationData,
        data: compact(invoiceMergeConflicts),
      };
    }
  }

  buildSkippedResponse(
    data: IPatientInvoiceV2JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  private _buildBalanceDiscrepancyResponse(
    patient: IGetRecordResponse<ExactPatient>,
    updateData: IInvoiceUpdateData[],
    failData: IBalanceDiscrepancyFailData
  ): IDestinationEntityRecord<IPatientInvoiceV2DestinationRecord> &
    MigratedWithErrorsDestinationEntityRecord<
      IPatientInvoiceV2DestinationRecord,
      IBalanceDiscrepancyFailData
    > {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      sourceRef: patient.record.id,
      status: DestinationEntityRecordStatus.MigratedWithErrors,
      data: {
        sourceRef: patient.record.id,
        invoiceData: updateData,
      },
      migratedAt: toTimestamp(),
      failData,
    };
  }

  private async _upsertInvoices(
    migrationData: IPatientInvoiceV2MigrationData,
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>
  ): Promise<IInvoiceUpdateData[]> {
    /**
     * Load existing account credits once up-front (non-transactional reads can
     * happen outside the runTransaction block). New credits created during
     * this run are tracked in-memory so subsequent invoices in the loop can
     * see them.
     */
    const persistedCredits = await Firestore.getDocs(
      AccountCredit.col({ ref: patientRef })
    );
    const knownCredits: WithRef<IAccountCredit>[] = persistedCredits.filter(
      (credit) => !credit.deleted
    );

    return Firestore.runTransaction(async (firestoreTransaction) => {
      return resolveSequentially(
        migrationData.data,
        async (invoiceBuildData) => {
          const invoiceDestinationRef = await translationMap.getDestination(
            invoiceBuildData.invoice.uid,
            PATIENT_TRANSACTION_RESOURCE_TYPE
          );

          const invoiceRef = await FirestoreMigrate.upsertDoc(
            Patient.invoiceCol({
              ref: patientRef,
            }),
            { ...invoiceBuildData.invoice },
            invoiceDestinationRef?.id,
            firestoreTransaction
          );

          if (!invoiceDestinationRef) {
            await translationMap.upsert({
              sourceIdentifier: invoiceBuildData.invoice.uid,
              destinationIdentifier: invoiceRef,
              resourceType: PATIENT_TRANSACTION_RESOURCE_TYPE,
            });
          }

          const transactionRefs = await resolveSequentially(
            invoiceBuildData.transactions,
            (transaction) =>
              FirestoreMigrate.upsertDoc(
                Invoice.transactionCol({
                  ref: invoiceRef,
                }),
                transaction,
                transaction.uid,
                firestoreTransaction
              )
          );

          const accountCredits = Invoice.getDueCredits(
            { ...invoiceBuildData.invoice, ref: invoiceRef },
            knownCredits
          );

          const accountCreditRefs = await resolveSequentially(
            accountCredits,
            async (accountCredit) => {
              const ref = await FirestoreMigrate.upsertDoc(
                AccountCredit.col({
                  ref: patientRef,
                }),
                { ...accountCredit, deleted: false },
                accountCredit.depositUid,
                firestoreTransaction
              );
              /**
               * Keep the in-memory view consistent so later invoices in this
               * loop see credits created earlier in the same outer transaction.
               */
              knownCredits.push({
                ...accountCredit,
                deleted: false,
                ref,
              } as WithRef<IAccountCredit>);
              return ref;
            }
          );

          return {
            invoiceRef,
            transactionRefs,
            accountCreditRefs,
          };
        }
      );
    });
  }

  private async _upsertOverpaymentAccountCredits(
    patientRef: DocumentReference<IPatient>,
    accountCreditInputs: IExactV2AccountCreditInput[]
  ): Promise<void> {
    const expectedCreditUids = accountCreditInputs.map((credit) => credit.uid);
    const existingAccountCredits = await Firestore.getDocs(
      AccountCredit.col({ ref: patientRef })
    );
    const staleMigrationCredits = existingAccountCredits.filter(
      (credit) =>
        !credit.deleted &&
        credit.ref.id.startsWith(OVERPAYMENT_ACCOUNT_CREDIT_PREFIX) &&
        !expectedCreditUids.includes(credit.ref.id)
    );

    await resolveSequentially(staleMigrationCredits, (credit) =>
      FirestoreMigrate.deleteDoc(credit.ref)
    );

    await resolveSequentially(
      accountCreditInputs,
      async (accountCreditInput) => {
        /**
         * Build the credit manually to avoid the practiceRef requirement in
         * AccountCredit.init. practiceRef is optional on IAccountCredit and we
         * do not have a reliable practice ref here without an extra mapping
         * lookup.
         */
        const credit: IAccountCredit = {
          description: accountCreditInput.description,
          amount: accountCreditInput.amount,
          type: AccountCreditType.Overpayment,
          used: 0,
          reservedFor: {},
          deleted: false,
        };
        await FirestoreMigrate.upsertDoc(
          AccountCredit.col({ ref: patientRef }),
          { ...credit, deleted: false },
          accountCreditInput.uid
        );
      }
    );
  }

  private _buildSuccessResponse(
    patient: IGetRecordResponse<ExactPatient>,
    updateData?: IInvoiceUpdateData[]
  ): IDestinationEntityRecord<IPatientInvoiceV2DestinationRecord> {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      data: {
        sourceRef: patient.record.id,
        invoiceData: updateData ?? [],
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
